import { isFixedLayoutTemplate } from 'app/utils/utils';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getCourseItemsService } from 'core/services/courseItems.service';
import * as coreEntities from 'core/store/index';
import { getCustomizedBy } from 'core/utils/course';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import { isDisregardedProgram } from 'core/utils/user';
import { CONTENT_TYPES, LESSON_TYPES_IDS } from 'core/variables/constant';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

export type QuizLocationCourse = {
  guid: string;
  has_power_lesson?: number;
  school_group_guid?: string | null;
  template?: { template_type?: string };
  users?: unknown[];
};

type QuizLocationLesson = {
  guid: string;
  lesson_guid?: string;
  lesson_name?: string;
  lesson_type?: string;
};

type QuizLocationUnit = {
  guid: string;
  is_accessible?: 0 | 1;
  items?: QuizLocationLesson[];
  lesson_guid?: string;
  lesson_name?: string;
  lesson_type?: string;
  section?: { guid?: string; name?: string } | string;
};

type ModuleOption = { data: QuizLocationUnit; label: string; value: string };
type SubmoduleOption = { data: QuizLocationLesson; label: string; value: string };

interface UseDialogQuizLocationParams {
  course: QuizLocationCourse;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * useDialogQuizLocation - Logic for picking the module/submodule of the program
 * where a new quiz will be created (Home entry point, TAN-8587) and navigating
 * to the quiz creation page.
 */
function useDialogQuizLocation({ course, isOpen, onClose }: UseDialogQuizLocationParams) {
  const { t } = useTranslation(['course', 'actions']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { schoolGuid, user } = useUser();
  const { enableOverlays } = useSystemSettings();

  const [units, setUnits] = useState<QuizLocationUnit[]>([]);
  const [selectedModule, setSelectedModule] = useState<ModuleOption | null>(null);
  const [selectedSubmodule, setSelectedSubmodule] = useState<SubmoduleOption | null>(null);
  const [isLoadingUnits, setIsLoadingUnits] = useState<boolean>(false);
  const [hasLoadedUnits, setHasLoadedUnits] = useState<boolean>(false);

  // Load the course units on open; reset everything on close
  useEffect(() => {
    if (!isOpen) {
      setUnits([]);
      setSelectedModule(null);
      setSelectedSubmodule(null);
      setHasLoadedUnits(false);
      return;
    }

    // enableOverlays decide el endpoint (overlays vs base): esperar a que los system
    // settings resuelvan para no cargar la versión base del programa por error
    if (enableOverlays === undefined) return;

    // Evita escribir estado obsoleto si se cierra/reabre antes de resolver la petición
    let isCancelled = false;

    async function loadUnits(): Promise<void> {
      setIsLoadingUnits(true);
      try {
        // Misma fuente de datos que el programa/libro (con overlays y personalización),
        // para que módulos y nombres coincidan con lo que ve el profesor en el programa
        const response = (await getCourseItemsService(
          course.guid,
          schoolGuid,
          getCustomizedBy(course, user),
          enableOverlays,
          isDisregardedProgram(course)
        )) as { items?: QuizLocationUnit[] } | QuizLocationUnit[] | null;
        if (isCancelled) return;
        const items = Array.isArray(response) ? response : (response?.items ?? []);
        setUnits(items);
      } catch (error) {
        if (isCancelled) return;
        console.error('[DialogQuizLocation] Error loading units:', error);
        setUnits([]);
      } finally {
        if (!isCancelled) {
          setIsLoadingUnits(false);
          setHasLoadedUnits(true);
        }
      }
    }

    loadUnits();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, course.guid, enableOverlays]);

  const isFixedLayout = isFixedLayoutTemplate(course.template?.template_type);

  // En el libro fixed layout, los módulos con is_accessible !== 1 se muestran como
  // "Próximamente" y no son accesibles: tampoco deben ofrecerse para crear un quiz
  const availableUnits = useMemo<QuizLocationUnit[]>(() => {
    return isFixedLayout ? units.filter((unit) => unit.is_accessible === 1) : units;
  }, [units, isFixedLayout]);

  const moduleOptions = useMemo<ModuleOption[]>(() => {
    return availableUnits.map((unit, index) => {
      const section = unit.section;
      const sectionGuid = (typeof section === 'object' ? section?.guid : undefined) || unit.guid;
      // En cursos fixed layout `section` llega a null y el nombre del módulo viene en `lesson_name`
      const sectionName = (typeof section === 'object' ? section?.name : section) || unit.lesson_name;

      return {
        value: sectionGuid,
        label: decodeHtmlWithWhiteList(sectionName ?? '') || String(index + 1),
        data: unit,
      };
    });
  }, [availableUnits]);

  // Submodules of the selected module (MINT lessons use a different flow)
  const submodules = useMemo<QuizLocationLesson[]>(() => {
    const moduleLessons = selectedModule?.data?.items ?? [];
    return moduleLessons.filter((lesson) => lesson.lesson_type !== LESSON_TYPES_IDS.MINT);
  }, [selectedModule]);

  const submoduleOptions = useMemo<SubmoduleOption[]>(() => {
    return submodules.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name ?? '') || String(index + 1),
      data: lesson,
    }));
  }, [submodules]);

  const hasSubmodules = submodules.length > 0;
  const hasContentError = hasLoadedUnits && !isLoadingUnits && units.length === 0;
  const canCreate = Boolean(selectedModule && (!hasSubmodules || selectedSubmodule));
  const isModuleSelectDisabled = moduleOptions.length === 0 && !isLoadingUnits;
  const moduleError = hasContentError ? t('course:quiz_location_content_unavailable_title') : undefined;

  function onChangeModule(option: ModuleOption): void {
    setSelectedModule(option);
    setSelectedSubmodule(null);
  }

  function onChangeSubmodule(option: SubmoduleOption): void {
    setSelectedSubmodule(option);
  }

  function onCreate(): void {
    if (!selectedModule) return;
    if (hasSubmodules && !selectedSubmodule) return;

    // El `value` de la opción de módulo es el guid de la sección/item del curso;
    // para navegar hace falta el guid de la lesson (`lesson_guid`, con `guid` como fallback)
    const selectedOption = hasSubmodules ? selectedSubmodule : selectedModule;
    const lessonGuid = selectedOption?.data?.lesson_guid || selectedOption?.data?.guid;
    if (!lessonGuid) return;

    const courseGuid = course.guid;
    // Los cursos con template fixed/híbrida son siempre power lesson, aunque los datos
    // del backend no traigan lesson_type en los items ni has_power_lesson en el curso
    const isPowerLesson = isFixedLayout || selectedOption?.data?.lesson_type === LESSON_TYPES_IDS.POWER || course.has_power_lesson === 1;
    const creationUrl = isPowerLesson
      ? `/course/${courseGuid}/lesson/${lessonGuid}?addQuiz=true`
      : `/course/${courseGuid}/lesson/${lessonGuid}/add/${CONTENT_TYPES.TEST}`;

    // Al guardar el quiz, la vista de assessment navega a `from` (history duck):
    // apuntarlo a la unidad elegida para volver al programa con el item nuevo seleccionado.
    dispatch(coreEntities.history.actions.setFrom(`/course/${courseGuid}/lesson/${lessonGuid}`));
    // Si una visita previa a una unidad dejó el flag power en true, el guardado no navegaría.
    dispatch(coreEntities.history.actions.setIsPowerLessonMode(false));

    history.push(creationUrl);
    onClose();
  }

  return {
    canCreate,
    hasContentError,
    hasSubmodules,
    isLoadingUnits,
    isModuleSelectDisabled,
    moduleError,
    moduleOptions,
    onChangeModule,
    onChangeSubmodule,
    onCreate,
    selectedModule,
    selectedSubmodule,
    submoduleOptions,
    t,
  };
}

export default useDialogQuizLocation;
